import * as React from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";
import useWebSocket from "react-use-websocket";
import { openSnackbar } from "../../Actions/ActionFunctions";
import { GamePlayer, SnackbarStatus, SocketMessageType } from "../../Definitions/Definitions";
import { useOriginalGame } from "./OriginalGameState";

export enum GameType {
    Original = 0
}

export interface BaseGameState {
    playerList: GamePlayer[];
    localPlayer: GamePlayer | null;
    actualLocalPlayer: GamePlayer | null;
    hostRoomId: number;
    gameStarted: boolean;
    lobbyOpen: boolean;
    switchingSides: boolean;
}

export interface GameContext {
    gameType: GameType;
    gameState: BaseGameState;
    updateGameState: (value: React.SetStateAction<any>) => void;
    startGame: () => void;
    sendMessage: (type: SocketMessageType, data: string) => void;
}

interface State {
    gameType: GameType;
}

const GameContext = React.createContext({} as GameContext);
export const GameContextProvider: React.FunctionComponent<{}> = (props) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { roomIdString, name } = useParams<{ roomIdString: string; name: string; }>();

    const {
        sendMessage,
        lastMessage,
        readyState,
    } = useWebSocket(`wss://${location.host}/ws?room=${roomIdString}&name=${name}`, {
        onOpen: () => { console.log("Websocket connected"); },
        onClose: (e) => {
            console.log(`Websocket close: ${e.reason}`);
            history.push("/");
            if (e.reason != "") {
                dispatch(openSnackbar(SnackbarStatus.Error, `Connection closed: ${e.reason}`, 3000));
            }
        },
        onError: (e) => {
            console.log(`Websocket error`, e);
            history.push("/");
            dispatch(openSnackbar(SnackbarStatus.Error, "Your connection has been lost", 3000));
        }
    });

    const sendMessageType = (type: SocketMessageType, data: string) => {
        sendMessage(JSON.stringify({ type, data }));
    }

    const { originalGameState, updateOriginalGameState, originalGameHandleMessage } = useOriginalGame({ sendMessage: sendMessageType });

    const [state, setState] = React.useState<State>({
        gameType: GameType.Original
    });

    const selectGameState = () => {
        switch (state.gameType) {
            default: return {} as BaseGameState;
            case GameType.Original: return originalGameState;
        }
    }

    const updateGameState = (value: React.SetStateAction<any>) => {
        switch (state.gameType) {
            default: break;
            case GameType.Original: updateOriginalGameState(value);
        }
    }

    const processMessage = (event: MessageEvent) => {
        let jsonString = "";
        if (typeof (event.data) == "string")
            jsonString = event.data;
        else {
            return;
        }

        const msg = JSON.parse(jsonString);
        const type = msg.type as SocketMessageType;
        const data = msg.data;

        switch (state.gameType) {
            default: break;
            case GameType.Original: originalGameHandleMessage(type, data); break;
        }
    }

    const startGame = () => {
        sendMessageType(SocketMessageType.StartGame, "");
    }

    React.useEffect(() => {
        if (lastMessage)
            processMessage(lastMessage);
    }, [lastMessage]);

    React.useEffect(() => {
        // send heartbeat in order to keep websocket connection alive
        const interval = setInterval(() => {
            sendMessageType(SocketMessageType.Heartbeat, "");
        }, 1000 * 85); // 85 seconds

        return () => clearInterval(interval);
    }, [])

    return (
        <GameContext.Provider
            value={{
                ...state,
                gameState: selectGameState(),
                updateGameState,
                sendMessage: sendMessageType,
                startGame
            }}
        >
            {props.children}
        </GameContext.Provider>
    );
}

GameContextProvider.displayName = "GameContextProvider";

export const useGameContext = () => React.useContext(GameContext);